import IndustrySector from "components/company/IndustrySector";
import CompanyActionsDropdown from "components/company/v2/CompanyActionsDropdown";
import InBookmarksTag from "components/company/v2/InBookmarksTag";
import InPortfolioTag from "components/company/v2/InPortfolioTag";
import CompanyDescription from "components/company/v3/CompanyDescription";
import BrazilianExternalLinks from "components/company/v3/brazilian/contents/about/BrazilianExternalLinks";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import Rating from "components/rating/Rating";
import classNames from "components/ui/classNames";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import { FormattedMessage } from "react-intl";
import { IAssetType, ISecurityCountry } from "types/graphqlTypes";

interface BrazilianAboutContentProps {
  tickerSymbol: string;
}

export default function BrazilianAboutContent({ tickerSymbol }: BrazilianAboutContentProps) {
  const selfie = useSelfie();
  const issuer = useBrazilianIssuer();

  return (
    <div className="flex w-full flex-col space-y-6 font-aeonik">
      <div className="flex flex-col items-center space-y-3 md:items-start">
        <div className="flex flex-col justify-center gap-3 md:flex-row md:justify-start md:gap-4">
          {issuer.niceLogo && (
            <div className="flex justify-center md:justify-start">
              <img
                role="presentation"
                src={issuer.niceLogo.url}
                width={54}
                height={54}
                alt={issuer.name}
                draggable={false}
                className="select-none object-contain object-right squircle"
              />
            </div>
          )}

          <div className="flex flex-col space-y-3 md:space-y-0.5">
            <div className="flex w-full flex-col items-center justify-center gap-3 md:flex-row md:justify-start">
              <h1
                className={classNames(
                  "m-0 block overflow-hidden text-ellipsis whitespace-nowrap text-2xl font-bold text-gray-800",
                  "max-w-72 sm:max-w-96 md:max-w-xl",
                  "dark:text-slate-200",
                )}
              >
                {issuer.name}
              </h1>

              <div className="flex space-x-3">
                {issuer.isBrazilianDepositaryReceipt && (
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger>
                        <span
                          title="Brazilian Depositary Receipt"
                          className="rounded-lg bg-blue-600 px-2 py-1 text-sm font-bold italic text-white"
                        >
                          BDR
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{getBDRTooltipContent(issuer, tickerSymbol)}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {selfie && (
                  <CompanyActionsDropdown
                    issuerId={issuer._id}
                    tickerSymbol={tickerSymbol}
                    securityCountry={ISecurityCountry.Br}
                    viewerDidBookmark={issuer.viewerDidBookmark}
                    isInPortfolio={issuer.isInPortfolio}
                  />
                )}

                {issuer.isInPortfolio && <InPortfolioTag />}
                {issuer.viewerDidBookmark && <InBookmarksTag />}
              </div>
            </div>

            <IndustrySector
              assetType={IAssetType.BrazilianCompany}
              industrySector={issuer.sector}
              className="tracking-wide"
            />
          </div>
        </div>

        <BrazilianExternalLinks />
      </div>

      <CompanyDescription tickerSymbol={tickerSymbol} country={ISecurityCountry.Br} />

      {selfie?.isPremium && !selfie?.isTrialing && (
        <Rating
          issuer={issuer}
          rankingPlacement={issuer.overallRankingPlacement}
          rankingRatingSummary={issuer.overallRankingRatingSummary}
        />
      )}
    </div>
  );
}

function getBDRTooltipContent(issuer: IBrazilianCompanyIssuerTopLevelFragment, tickerSymbol: string) {
  const security = issuer.securities.find((candidate) => {
    return candidate.tickerSymbol === tickerSymbol;
  });

  if (security && security.depositoryReceiptRatio) {
    return (
      <FormattedMessage
        defaultMessage="Proporção: {depositoryReceiptRatio}"
        description="Tooltip para tag BDR"
        id="4T2qmy"
        values={{
          depositoryReceiptRatio: security.depositoryReceiptRatio,
        }}
      />
    );
  }
  return "Brazilian Depositary Receipt";
}
